/**
 * Typed endpoint wrappers for the Cascade V1 read surface. Shapes come from
 * @command-center/leadloop-core; the transport lives in ./api.
 */

import type {
  DashboardCards,
  LccContact,
  LccFormSubmission,
  LccInteractionInput,
  LccLead,
  LccOrganization,
  LoopReason,
} from '@command-center/leadloop-core';
import type {
  ActivityApiRow,
  ActivityListResponse,
  ActivityQueryParams,
  AiLogListResponse,
  AiLogQueryParams,
  AiUsageResponse,
} from '@command-center/logging-sdk';
import { request, type PagedResponse } from './api';

export type {
  DashboardCards,
  LccContact,
  LccFormSubmission,
  LccInteractionInput,
  LccLead,
  LccOrganization,
  LoopReason,
};

export interface ListResponse<T> {
  items: T[];
  total: number;
}

export type { PagedResponse };

/** Server-derived roll-ups the Contacts table shows without opening a record. */
export interface CascadeContactListItem extends CascadeContact {
  organizationName?: string;
  openLeadCount: number;
  totalLeadCount: number;
  lastActivityAt?: string;
}

/** Server-derived roll-ups the Organizations table shows without opening one. */
export interface CascadeOrganizationListItem extends LccOrganization {
  primaryContactName?: string;
  primaryContactEmail?: string;
  primaryContactPhone?: string;
  contactCount: number;
  openLeadCount: number;
  totalLeadCount: number;
  lastActivityAt?: string;
}

export interface PageRequest {
  page?: number;
  pageSize?: number;
}

function pageParams(params: URLSearchParams, page?: PageRequest): void {
  if (page?.page && page.page > 1) params.set('page', String(page.page));
  if (page?.pageSize) params.set('pageSize', String(page.pageSize));
}

/**
 * An unconfirmed Call or Form Submission awaiting triage. It rides in the same
 * queue as leads but is NOT a lead — no business record exists for it yet.
 */
export interface IntakeItem {
  id: string;
  sourceType: 'call' | 'form_submission';
  sourceId: string;
  occurredAt: string | null;
  displayName: string | null;
  email: string | null;
  phone: string | null;
  company: string | null;
  summary: string | null;
  aiRecommendation: string | null;
  aiConfidence: number | null;
  callPurpose: string | null;
  callerRelationship: string | null;
  leadEligibility: string | null;
  archived: boolean;
  archivedAt: string | null;
  status: string;
  disposition: string | null;
  resolvedAt: string | null;
  createdAt: string;
  /**
   * Where this record stands, derived server-side: `new` (nobody has touched
   * it), `open` (noted but still undecided), `resolved`, or `dismissed`.
   *
   * Optional because the field postdates the DTO — an older cached response
   * simply renders no lifecycle badge rather than a wrong one.
   */
  lifecycle?: IntakeLifecycle;
  /** "Lead Created" / "Linked to Lead" / "Dismissed". Absent while open. */
  resolutionLabel?: string;
}

export type IntakeLifecycle = 'new' | 'open' | 'resolved' | 'dismissed';

/**
 * One item in the Lead Follow-up queue: always a Lead. Actionable Calls and Form
 * Entries are loaded independently for the intake section on the same dashboard.
 * `kind` stays on the wire because the server's feed shape is shared with the Lead
 * Command Center.
 */
export interface LoopItem {
  kind: 'lead';
  lead: LccLead;
  reason: LoopReason;
  dueAt?: string;
}

export interface LoopResponse extends ListResponse<LoopItem> {
  cards: DashboardCards;
}

export interface IntakeMatchCandidate<T> {
  record: T;
  score: number;
  reasons: string[];
}

export interface IntakeLeadMatch {
  id: string;
  title: string;
  contactName: string | null;
  status: string;
}

export interface IntakeDetailResponse {
  intake: IntakeItem;
  /** The verbatim call or submission row — transcript, recording, full fields. */
  source: Record<string, unknown> | null;
  /**
   * A generated Lead title summarising the request. Absent on already-resolved
   * intake, and always editable before the Lead is created.
   */
  suggestedTitle?: string;
  links: Array<{
    id: string;
    target_type: string;
    target_record_id: string;
    link_reason: string;
  }>;
  matches: {
    contacts: IntakeMatchCandidate<CascadeContact>[];
    leads: IntakeMatchCandidate<IntakeLeadMatch>[];
  };
  dismissalOptions: string[];
}

export interface IntakeSourceResponse {
  intake: IntakeItem | null;
  lead: { id: string; relationship: string } | null;
  contacts: CascadeContact[];
}

export type BulkIntakeRecord =
  | { intakeId: string; sourceType?: never; sourceId?: never }
  | { intakeId?: never; sourceType: IntakeItem['sourceType']; sourceId: string };

export interface BulkActionResponse {
  ok: boolean;
  succeeded: number;
  failed: number;
  results: Array<Record<string, unknown> & { ok: boolean; error?: string }>;
}

export interface LeadDetailResponse {
  lead: LccLead;
  contact?: CascadeContact;
  organization?: LccOrganization;
  submission?: LccFormSubmission;
  submissions: LccFormSubmission[];
  /** Calls and form entries this lead was created from or linked to. */
  intake: IntakeItem[];
}

export interface CascadeContact extends LccContact {
  callerContactId?: string;
  leadContactId?: string;
  aiOverview?: string;
}

export interface ContactOverviewStatus {
  status: 'idle' | 'pending' | 'processing' | 'failed';
  enabled: boolean;
  lastRefreshedAt?: string;
  lastError?: string;
}

export interface ContactDetailResponse {
  contact: CascadeContact;
  organization?: LccOrganization;
  leads: LccLead[];
  submissions: LccFormSubmission[];
  /** Calls and form entries explicitly linked to this Contact. */
  intake: IntakeItem[];
  overviewStatus?: ContactOverviewStatus;
}

export interface OrganizationDetailResponse {
  organization: LccOrganization;
  contacts: CascadeContact[];
  leads: LccLead[];
  submissions: LccFormSubmission[];
}

/**
 * A record's timeline entry, as the record detail views consume it.
 *
 * The API now serves the shared `ActivityApiRow` contract; this shape is kept
 * and adapted into because six record views render it, and their presentation
 * is not what this pass is changing. New surfaces should read `ActivityApiRow`
 * directly through `activityLogs` below.
 */
export interface ActivityItem {
  id?: string;
  ts?: string;
  action: string;
  summary: string;
  category?: string;
  actor?: { name?: string; email?: string; type?: string };
  entity?: { type: string; id?: string; label?: string };
  metadata?: Record<string, unknown>;
}

export const leadloopApi = {
  loop: () => request<LoopResponse>('/api/leads/loop'),
  leads: (opts: { status?: string; q?: string } & PageRequest = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    pageParams(params, opts);
    const qs = params.toString();
    return request<PagedResponse<LccLead>>(`/api/leads${qs ? `?${qs}` : ''}`);
  },
  leadDetail: (id: string) => request<LeadDetailResponse>(`/api/leads/${encodeURIComponent(id)}`),
  contacts: (q?: string, page?: PageRequest) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    pageParams(params, page);
    const qs = params.toString();
    return request<PagedResponse<CascadeContactListItem>>(`/api/contacts${qs ? `?${qs}` : ''}`);
  },
  contactDetail: (id: string) =>
    request<ContactDetailResponse>(`/api/contacts/${encodeURIComponent(id)}`),
  refreshContactOverview: (id: string, idempotencyKey: string) =>
    request<{
      overview: string;
      changed: boolean;
      lastRefreshedAt?: string;
    }>(`/api/contacts/${encodeURIComponent(id)}/ai-overview/refresh`, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
    }),
  organizations: (q?: string, page?: PageRequest) => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    pageParams(params, page);
    const qs = params.toString();
    return request<PagedResponse<CascadeOrganizationListItem>>(
      `/api/organizations${qs ? `?${qs}` : ''}`,
    );
  },
  organizationDetail: (id: string) =>
    request<OrganizationDetailResponse>(`/api/organizations/${encodeURIComponent(id)}`),
  activity: async (opts: { entityId?: string; limit?: number } = {}) => {
    const params = new URLSearchParams();
    if (opts.entityId) params.set('entityId', opts.entityId);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    const page = await request<ActivityListResponse>(`/api/activity${qs ? `?${qs}` : ''}`);
    const items = page.items.map(toActivityItem);
    return { items, total: items.length } satisfies ListResponse<ActivityItem>;
  },
  /** The raw shared contract, for the Activity page's viewers. */
  activityLogs: (params: ActivityQueryParams) =>
    request<ActivityListResponse>(`/api/activity${toLogQuery(params)}`),
  aiLogs: (params: AiLogQueryParams) =>
    request<AiLogListResponse>(`/api/ai-logs${toLogQuery(params)}`),
  aiUsage: (params: AiLogQueryParams) =>
    request<AiUsageResponse>(`/api/ai-logs/usage${toLogQuery(params)}`),
  addNote: (input: {
    entityType: string;
    entityId: string;
    entityLabel: string;
    note: string;
  }) =>
    request<{ ok: true }>('/api/activity/notes', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  linkContactToLead: (contactId: string, leadId: string) =>
    request<{ lead: LccLead; contact: CascadeContact }>(
      `/api/contacts/${encodeURIComponent(contactId)}/leads/${encodeURIComponent(leadId)}`,
      { method: 'POST' },
    ),

  // ---------- writes (gated server-side behind write_actions_enabled) ----------
  recordInteraction: (leadId: string, input: LccInteractionInput) =>
    request<{ lead: LccLead }>(`/api/leads/${encodeURIComponent(leadId)}/interactions`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  changeStatus: (leadId: string, status: string) =>
    request<{ lead: LccLead }>(`/api/leads/${encodeURIComponent(leadId)}/status`, {
      method: 'POST',
      body: JSON.stringify({ status }),
    }),
  archiveLead: (leadId: string, archived: boolean) =>
    request<{ lead: LccLead }>(`/api/leads/${encodeURIComponent(leadId)}/${archived ? 'archive' : 'restore'}`, {
      method: 'POST',
    }),
  setFollowUp: (leadId: string, input: { nextFollowUpAt?: string | null; nextStep?: string }) =>
    request<{ lead: LccLead }>(`/api/leads/${encodeURIComponent(leadId)}/follow-up`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ---------- intake triage ----------
  actionableIntake: () =>
    request<ListResponse<IntakeItem>>('/api/intake?status=actionable&limit=500'),
  intakeDetail: (id: string) =>
    request<IntakeDetailResponse>(`/api/intake/${encodeURIComponent(id)}`),
  intakeSource: (sourceType: IntakeItem['sourceType'], sourceId: string) =>
    request<IntakeSourceResponse>(
      `/api/intake/source/${encodeURIComponent(sourceType)}/${encodeURIComponent(sourceId)}`,
    ),
  addIntakeNote: (sourceType: IntakeItem['sourceType'], sourceId: string, body: string) =>
    request(
      sourceType === 'call'
        ? `/api/voice/calls/${encodeURIComponent(sourceId)}/notes`
        : `/api/form-submissions/${encodeURIComponent(sourceId)}/notes`,
      { method: 'POST', body: JSON.stringify({ body }) },
    ),
  bulkDismissLeads: (ids: string[]) =>
    request<BulkActionResponse>('/api/leads/bulk', {
      method: 'POST',
      body: JSON.stringify({ action: 'dismiss', ids }),
    }).then(requireCompleteBulkAction),
  createLeadFromIntake: (
    id: string,
    input: {
      title?: string;
      contactName?: string | null;
      email?: string | null;
      phone?: string | null;
      companyName?: string | null;
      message?: string | null;
      contactId?: string | null;
    } = {},
  ) =>
    request<{ ok: true; created: boolean; alreadyResolved?: boolean; lead?: LccLead; leadRecordId?: string }>(
      `/api/intake/${encodeURIComponent(id)}/create-lead`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  linkIntakeToLead: (id: string, leadId: string) =>
    request<{ ok: true; alreadyResolved?: boolean; lead?: LccLead; leadRecordId?: string }>(
      `/api/intake/${encodeURIComponent(id)}/link-lead`,
      { method: 'POST', body: JSON.stringify({ leadId }) },
    ),
  dismissIntake: (id: string, disposition: string, notes?: string | null) =>
    request<{ ok: true; alreadyResolved?: boolean }>(
      `/api/intake/${encodeURIComponent(id)}/dismiss`,
      { method: 'POST', body: JSON.stringify({ disposition, notes }) },
    ),
  bulkIntake: (
    records: BulkIntakeRecord[],
    action: 'create_leads' | 'dismiss',
    input: { disposition?: string; notes?: string | null } = {},
  ) => request<BulkActionResponse>('/api/intake/bulk', {
    method: 'POST',
    body: JSON.stringify({ records, action, ...input }),
  }).then(requireCompleteBulkAction),
};

function requireCompleteBulkAction(result: BulkActionResponse): BulkActionResponse {
  if (result.failed === 0) return result;
  const firstFailure = result.results.find((item) => !item.ok)?.error;
  throw new Error(
    `${result.failed} of ${result.succeeded + result.failed} records could not be processed${firstFailure ? `: ${firstFailure}` : '.'}`,
  );
}

/**
 * Shared wire row → the shape the record timelines already render.
 *
 * Deliberately lossy: the record views want who did what to this record, not
 * the operational attribution the Activity page needs.
 */
function toActivityItem(row: ActivityApiRow): ActivityItem {
  return {
    id: row.id,
    ts: row.createdAt,
    action: row.action,
    summary: row.summary ?? row.action,
    category: row.category,
    actor: row.actorName
      ? { name: row.actorName, email: row.actorEmail, type: row.actorType }
      : undefined,
    entity: { type: row.entityType, id: row.entityId, label: row.entityLabel },
    metadata: row.metadata,
  };
}

function toLogQuery(params: object): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value));
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
